/**
 * The app's one session controller (./session) over the tab's database client, Supabase
 * Auth on the main thread (./supabase), the browser's fetch and connectivity, and the
 * wipe (./wipe). This is the DOM-bound half; ./session stays platform-neutral for the
 * tests. Screens and the guard import `sessionController()` from here.
 */
import { setApiLangkahListener, setApiTokenProvider } from '../api/client'
import { getDb } from '../app/db'
import { adaBelumDisimpan } from '../app/store/belumDisimpan'
import { lepasPenjagaTab } from '../app/store/jaga'
import { pasangPreferensiAkun } from '../app/store/preferensiAkun'
import { useSesi } from '../app/store/sesi'
import type { DbClient } from '../db/client'
import { DB_LOCK_NAME } from '../db/lock'
import { tokenUntukLayar } from '../sync/tokenPengguna'
import { bacaPenggunaPerangkat, tabPerluDimuatUlang } from './gantiPengguna'
import { pasangKunciDiam } from './kunciDiamTab'
import { createSessionController, type SessionController } from './session'
import { createSupabaseAuth } from './supabase'
import { wipeDevice } from './wipe'

/** "Chrome di Windows": the name an admin sees in Perangkat until they rename it. */
export function infoPerangkat(): { nama: string; platform: string | null } {
  if (typeof navigator === 'undefined') return { nama: 'Perangkat', platform: null }
  const nav = navigator as Navigator & {
    userAgentData?: { platform?: string; brands?: { brand: string }[] }
  }
  const ua = navigator.userAgent
  const platform =
    nav.userAgentData?.platform ??
    (/Android/i.test(ua)
      ? 'Android'
      : /iPhone|iPad/i.test(ua)
        ? 'iOS'
        : /Windows/i.test(ua)
          ? 'Windows'
          : /Mac/i.test(ua)
            ? 'macOS'
            : /Linux/i.test(ua)
              ? 'Linux'
              : null)
  const brand =
    nav.userAgentData?.brands?.find((b) => !/Not|Chromium/i.test(b.brand))?.brand ??
    (/Edg\//.test(ua)
      ? 'Edge'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Browser')
  return { nama: platform === null ? brand : `${brand} di ${platform}`, platform }
}

let instance: SessionController | null = null

export function sessionController(): SessionController {
  instance ??= createSessionController({
    db: getDb(),
    auth: createSupabaseAuth(),
    fetch: (input, init) => fetch(input, init),
    apiBase: import.meta.env.VITE_API_BASE || '/api',
    clientVersion: import.meta.env.VITE_APP_VERSION ?? '0.1.0',
    online: () => navigator.onLine,
    now: () => Date.now(),
    perangkat: infoPerangkat,
    layarSentuh: () => matchMedia('(pointer: coarse)').matches,
    wipe: () => wipeDevice(getDb()),
    // A form with unsaved changes, or a figure a field-by-field screen could not save yet.
    isianBelumDisimpan: adaBelumDisimpan,
  })
  return instance
}

/**
 * The reload a tab that took the database back owes its person: the browser's "leave site?"
 * question (a form with unsaved changes holds a closing tab) must never stand in front of it.
 */
function muatUlang(): void {
  lepasPenjagaTab()
  location.reload()
}

/**
 * Shared devices across tabs (spec 15.4). One tab holds the database (db/lock.ts); another
 * may take it over, sign someone else in, clear the device or sign out. A tab that takes the
 * database back reloads when it no longer serves this tab's person under this tab's scope,
 * instead of resuming that person's shell over another's data. A change of user in this tab
 * is announced on the lock's channel, so the other tabs lock to the PIN at once.
 */
function pantauTabLain(db: DbClient): void {
  let sebelumnya = db.lockState.status
  db.onLockState((lock) => {
    const kembali = lock.status === 'memegang' && sebelumnya === 'meminta'
    sebelumnya = lock.status
    // A tab that never held the database is still starting: start() reads it fresh.
    if (!kembali || useSesi.getState().status === 'memuat') return
    // A microtask later: the lock posts db:resume right after announcing the state.
    void Promise.resolve()
      .then(() => bacaPenggunaPerangkat(db))
      .then(
        (perangkat) => {
          if (tabPerluDimuatUlang(useSesi.getState().asli, perangkat)) muatUlang()
        },
        () => muatUlang(),
      )
  })
  if (typeof BroadcastChannel === 'undefined') return
  const kanal = new BroadcastChannel(DB_LOCK_NAME)
  useSesi.subscribe((state, previous) => {
    if (previous.sesi.userId !== '' && state.sesi.userId !== previous.sesi.userId) {
      // Legacy message key from SIMU: a tab of the old version hears it too (test/nama-warisan).
      kanal.postMessage({ simuPengguna: 'berubah' })
    }
  })
  kanal.addEventListener('message', (event) => {
    if ((event.data as { simuPengguna?: unknown } | null)?.simuPengguna !== 'berubah') return
    if (useSesi.getState().status === 'aktif') useSesi.setState({ status: 'terkunci' })
  })
}

let kunciDiamTerpasang = false

/** The idle lock (./kunciDiamTab) for the tab's life, installed once. */
function pasangKunciDiamSekali(): void {
  if (kunciDiamTerpasang) return
  kunciDiamTerpasang = true
  pasangKunciDiam(() => void sessionController().kunci())
}

/** Start the session as soon as the database client exists (main.tsx); resolves when known. */
export function startSession(): Promise<void> {
  // Plan 9b: each sign-in, unlock or change of person loads that person's display preferences
  // (installed before the session is read, so the first move into the app is seen too).
  pasangPreferensiAkun()
  const controller = sessionController()
  pasangKunciDiamSekali()
  // The app-to-API client (section 13) signs requests with the same session token as the
  // sync loop, and only while that token is the signed-in person's (spec 15.4).
  setApiTokenProvider(
    tokenUntukLayar(controller.tokenProvider, () => useSesi.getState().asli.userId),
  )
  // A screen's API call refused for an account step opens that step, as a refused sync does.
  setApiLangkahListener((langkah) => useSesi.setState({ langkahAkun: langkah }))
  pantauTabLain(getDb())
  return controller.start()
}
